﻿import React, { useState, useEffect } from 'react';
import { UserDataStore } from '../store/userDataStore';
import { DensityBenchSheet } from '../models/density-bench-sheet';
import { BenchSheetTechnologyType, NoteType, DocumentType, Entities } from '../models/enums';
import SecondaryBenchSheetInstrument from './SecondaryBenchSheetInstrument';
import BenchSheetHeaderComponent from './BenchSheetHeaderComponent';
import BasicReadingsData from './BasicReadingsData';
import BenchSheetData from './BenchSheetData';
import NoteViewer from './NoteViewer';
import NumberBoxComponent from './NumberBoxComponent';
import { FieldUpdate } from '../models/field-update';
import { CrudFunctions } from '../utilities/crud-functions';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';
import { ItemBasicInformation } from '../models/item-basic-information';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import TicketLogEntryForm from './TicketLogEntryForm';
import BenchSheetToolbar from './BenchSheetToolbar';
import { DocumentationConstants } from '../store/documentation-constants';
import DmaFooter from './DmaFooter';

import { Popup as DialogPopup } from 'devextreme-react/popup';

let key = 1;

export default function DensityBenchSheetForm() {
    const [canEdit, setCanEdit] =
        useState<boolean>(false);

    const [benchSheet, setBenchSheet] =
        useState<DensityBenchSheet>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [ticketLogEntryDialogVisible, setTicketLogEntryDialogVisible] =
        useState<boolean>();

    const [itemBasicInitInformation, setItemBasicInitInformation] =
        useState<ItemBasicInformation>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        setAuthorizations();
        getAllData();
    }, []);

    const parts = window.location.href.split('/');
    const benchSheetId = parts[parts.length - 1];

    key++;

    return (
        <div className="pw-content">
            <div>

                <BenchSheetToolbar title="Density Bench Sheet"
                    subtitle="Density"
                    auditRowPointer={benchSheetId} entity={Entities.DensityBenchSheets}
                    technologyId={BenchSheetTechnologyType.Density}
                    documentationId={DocumentationConstants.DENSITYBenchSheets}
                    showItemInformation={showBenchSheetInformationDialog}
                    showTicketLog={showTicketLogEntryDialog} />

                <BenchSheetHeaderComponent benchSheetId={benchSheetId} readOnly={!canEdit} />

                <SecondaryBenchSheetInstrument benchSheetId={benchSheetId} isReadOnly={!canEdit}
                    title="Density Instrument"
                    technology={BenchSheetTechnologyType.Density} />

                <div className="pw-comp-row">
                    <div className="pw-form-unit">
                        <div className="pw-form-unit-label">Temperature</div>
                        <NumberBoxComponent id="temperature"
                            cid="9131D58D-19F9-4F65-8CED-8975E7D3395C" readOnly={!canEdit}
                            format="#0.000" fixedUnit={'°C'}
                            onValueChanged={onValueChanged}
                            value={benchSheet ? benchSheet.temperature : undefined} />
                    </div>
                </div>

                <BasicReadingsData benchSheetId={benchSheetId} isReadOnly={!canEdit}
                    title="Density Readings" datagridHeight="12rem" />

                <div className="pw-comp-row-scroll-fixed-grid">
                    <NoteViewer height='10rem' title="Bench Sheet Note"
                        noteType={NoteType.Method} parentId={benchSheetId} />
                </div>

                <div className="pw-comp-row-scroll-fixed-grid">
                    <BenchSheetData parentId={benchSheetId}
                        readOnly={!canEdit} canDelete={true}
                        technology={BenchSheetTechnologyType.Density}
                        hideImageHandler={true}
                        documentType={DocumentType.General} />
                </div>

            </div>

            <DmaFooter />

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width={'25rem'}
                    height={'21rem'}
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInitInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {ticketLogEntryDialogVisible &&
                <DialogPopup visible={ticketLogEntryDialogVisible}
                    onHiding={hideTicketLogEntryDialog}
                    title="Ticket Log Entry"
                    width={'55rem'}
                    height={'45rem'}
                    dragEnabled={true}
                    resizeEnabled={true}>
                    <TicketLogEntryForm formName="HPLC Bench Sheet" contextData={benchSheet}
                        onCancel={hideTicketLogEntryDialog}
                        onComplete={hideTicketLogEntryDialog}
                        key={`TLE${key}`} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    function setAuthorizations() {
        const user = UserDataStore.getCurrentUser();

        const canEditRoles = ["SA", "MF-A", "MF-L", "MF-T", "PP-A", "PP-L", "PP-T", "DIST-A", "BA-A", "QCP-A", "QCD-A", "RD-A", "RD-L", "RD-T"];
        const canEdit = canEditRoles.some(r => user.roleCodes && user.roleCodes.includes(r));

        setCanEdit(canEdit);
    }

    async function onValueChanged(e: any) {
        if (!e.event) return;

        let caller;

        if (e.event.data && e.event.data.prop) {
            caller = e.event.data.prop as string;
        }
        else {
            caller = e.element.id as string;
        }

        let fieldUpdate: FieldUpdate = {
            entity: "DensityBenchSheets",
            field: caller,
            value: e.value,
            rowId: benchSheetId
        };

        await updateField(fieldUpdate)
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Updating Field");
            });

        setBenchSheet({ ...benchSheet, [caller]: e.value });
    }

    //GET methods

    async function getBenchSheet() {
        await CrudFunctions.getData(`benchsheets/benchsheet/${benchSheetId}`)
            .then(data => {
                setBenchSheet(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Bench Sheet Data");
            });
    }

    async function updateField(fieldUpdate: FieldUpdate) {
        return CrudFunctions.putData("benchsheets/benchsheetfield", fieldUpdate);
    }

    // other functions

    async function getAllData() {
        getBenchSheet();
    }

    // show/hide

    function showTicketLogEntryDialog() {
        setTicketLogEntryDialogVisible(true);
    }

    function hideTicketLogEntryDialog() {
        setTicketLogEntryDialogVisible(false);
    }

    function showBenchSheetInformationDialog() {
        if (benchSheet) {
            let itemBasicInitInformation: ItemBasicInformation = {
                rowPointer: benchSheet.benchSheetId,
                createTimestamp: benchSheet.createTimestamp,
                lastModifiedTimestamp: benchSheet.modifyTimestamp,
                createUser: benchSheet.createUser,
                modifyUser: benchSheet.modifyUser
            };

            setItemBasicInitInformation(itemBasicInitInformation);
            setItemBasicInformationDialogVisible(true);
        }
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}


